import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { DefaultPathEnum } from '../../../../main/constants/default-path.ts';
import { ErrorTypeEnum } from '../../../../main/constants/error-type.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../../main/utils/monitoring/sentry.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';
import { getUrlUtil } from '../../../../main/utils/url.util.ts';

import { createChildProfileMutationRequest } from '../../../requests/create-child-profile.mutation.ts';
import { ProfileCreationFormSchema } from '../../../types/accounts-validation.types.ts';

import { ACCOUNTS_SLICE_NAME, AccountsSliceState } from '../accounts.slice.types.ts';

import { fetchAndAssignChildProfileThunk } from './fetch-and-assign-child-profile.thunk.ts';

interface Props {
  name: string;
  age: number;
}

const THUNK_NAME = `${ACCOUNTS_SLICE_NAME}/createChildProfile`;

const handleError = (input: Props, message?: string): Error => {
  const errorMessage = message ?? getLocaleUtil().select('accounts:create_profile_failed');
  getToasterUtil().error(errorMessage);
  const error = new Error(errorMessage);
  error.name = ErrorTypeEnum.CREATE_PROFILE_ERROR;

  getSentryUtil().trackError(
    error,
    {
      thunk: THUNK_NAME,
      errorType: ErrorTypeEnum.CREATE_PROFILE_ERROR,
    },
    { input }
  );

  getTrackerUtil().track({
    event: TrackingEventEnum.CHILD_PROFILE_CREATION_FAILED,
  });
  return error;
};

export const createChildProfileThunk = createAsyncThunk<void, Props, ThunkApiConfigType>(
  THUNK_NAME,
  async (props, thunkAPI) => {
    const { name, age } = props;

    const parsedInput = ProfileCreationFormSchema.safeParse(props);
    if (!parsedInput.success) {
      const error = handleError(props);
      throw error;
    }

    const result = await createChildProfileMutationRequest(parsedInput.data);
    if (!result?.success) {
      const error = handleError(props, result.message);
      throw error;
    }

    getToasterUtil().success(getLocaleUtil().select('accounts:create_profile_success'));
    await thunkAPI.dispatch(fetchAndAssignChildProfileThunk()).unwrap();

    getTrackerUtil().track({
      event: TrackingEventEnum.CHILD_PROFILE_CREATED,
      data: {
        name,
        age: String(age),
      },
    });
  }
);

export const createChildProfileThunkExtraReducers = (builder: ActionReducerMapBuilder<AccountsSliceState>): void => {
  builder.addCase(createChildProfileThunk.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(createChildProfileThunk.fulfilled, (state) => {
    state.isLoading = false;
    state.shouldSeeBeginJourney = true;

    getUrlUtil().navigateToUrl(DefaultPathEnum.ACCOUNTS_BEGIN_JOURNEY, true);
  });

  builder.addCase(createChildProfileThunk.rejected, (state) => {
    state.isLoading = false;
  });
};
